/**
 * Represents a contact flowing through an automation for testing and visualization.
 *
 * Flow contacts are used in the admin automation testing interface to simulate
 * and visualize how real contacts would progress through an automation. Each
 * contact is assigned a unique color to track their path through the flow diagram.
 *
 * Used by:
 * - TestingContactsDrawerContent component for selecting test contacts
 * - Flow visualization to show contact positions and paths
 * - Switch node evaluation to determine which branch each contact takes
 *
 * @example
 * ```typescript
 * const testContact: FlowContact = {
 *   id: 'sample-1',
 *   firstName: 'John',
 *   lastName: 'Smith',
 *   tags: ['VIP', 'Client'],
 *   keyFacts: ['Birthday: March 15', 'Policy Renewal: June 2025'],
 *   color: '#6366f1',
 *   isActive: true
 * };
 * ```
 */
export interface FlowContact {
	/** Unique identifier for the contact */
	id: string;
	/** Contact's first name */
	firstName: string;
	/** Contact's last name */
	lastName: string;
	/** Optional URL to profile picture */
	profilePic?: string;
	/** Array of tag names applied to this contact (used for conditional logic) */
	tags: string[];
	/** Array of key facts or notes about the contact (for display purposes) */
	keyFacts: string[];
	/** Hex color code assigned to this contact for visual tracking in the flow */
	color: string;
	/** Whether this contact is currently selected and flowing through the automation */
	isActive: boolean;
}

/**
 * Contact Colors for Visual Flow Testing
 *
 * These colors are carefully chosen for use in the admin automation testing interface.
 * They remain as hardcoded constants rather than centralized theme colors because:
 *
 * 1. **Visual Distinction**: Each contact needs a unique, distinguishable color to help
 *    visualize different contacts flowing through the automation in testing scenarios.
 *
 * 2. **Accessibility**: This palette is designed to be colorblind-friendly and provides
 *    sufficient contrast against white backgrounds (WCAG AA compliant).
 *
 * 3. **Testing Consistency**: These colors map to specific sample contacts and should remain
 *    consistent across testing sessions to help users track the same contacts through flows.
 *
 * 4. **Data Visualization Purpose**: Contact colors serve a specialized visualization
 *    purpose for testing automation flows, distinct from brand/UI colors, similar to how
 *    BRANCH_COLORS serves conditional path visualization.
 *
 * If modifying these colors, ensure:
 * - Minimum contrast ratio of 4.5:1 against white background
 * - Colors are distinguishable for users with color vision deficiencies
 * - Total count supports MAX_CONTACTS (currently 10)
 */
export const CONTACT_COLORS = [
	'#6366f1', // indigo
	'#ec4899', // pink
	'#10b981', // green
	'#f59e0b', // amber
	'#3b82f6', // blue
	'#8b5cf6', // purple
	'#ef4444', // red
	'#14b8a6', // teal
	'#f97316', // orange
	'#a855f7', // violet
] as const;
